import { Row, Col, Card, Statistic, Table, Tag, Alert, Typography, Spin, Button, Space, List, Tooltip, message } from 'antd'
import {
  ToolOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  WarningOutlined,
  ArrowRightOutlined,
  RobotOutlined,
  InfoCircleOutlined,
  InboxOutlined,
  MailOutlined,
  NotificationOutlined,
  QuestionCircleOutlined,
  AuditOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { formatDateTime } from '@/lib/format'
import client from '@/api/client'
import { interventiApi, MOTIVI_SOSPENSIONE_SLA } from '@/api/interventi'
import { notificheApi } from '@/api/notifiche'
import * as intakeCodaApi from '@/api/intakeCoda'
import type { KpiDashboard } from '@/types'

const { Title } = Typography

const HelpTip = ({ text }: { text: string }) => (
  <Tooltip title={text}>
    <QuestionCircleOutlined
      style={{ marginLeft: 6, color: '#bbb', cursor: 'help', fontSize: 12 }}
    />
  </Tooltip>
)

const KPI_CONFIG = [
  { key: 'ticket_aperti',          label: 'Ticket aperti',    icon: <InboxOutlined />,              color: '#1677ff', help: 'Ticket ricevuti e non ancora presi in carico (stato "aperto").' },
  { key: 'in_lavorazione',         label: 'In lavorazione',   icon: <ToolOutlined />,               color: '#fa8c16', help: 'Interventi attualmente in corso (stato "aperto" o "in esecuzione").' },
  { key: 'in_attesa_doc',          label: 'In att. doc.',     icon: <ClockCircleOutlined />,        color: '#faad14', help: 'Interventi conclusi sul campo, in attesa che il terzista carichi la documentazione (bolla, foto, formulario, consuntivo).' },
  { key: 'interventi_scaduti',     label: 'Scaduti SLA',      icon: <ExclamationCircleOutlined />,  color: '#ff4d4f', help: 'Interventi con SLA superato ancora da gestire (stati: aperto, in esecuzione, da validare). Esclusi quelli in attesa documentazione, chiusi e annullati.' },
  { key: 'interventi_chiusi_oggi', label: 'Validati oggi',    icon: <CheckCircleOutlined />,        color: '#52c41a', help: 'Interventi validati (passati a "chiuso") nella giornata di oggi.' },
  { key: 'da_validare',            label: 'Da validare',      icon: <FileTextOutlined />,           color: '#722ed1', help: 'Interventi terminati e documentati, con bolla pronta per la validazione del coordinatore.' },
  { key: 'in_verifica_costo',      label: 'In verifica costo', icon: <AuditOutlined />,             color: '#d4380d', help: 'Interventi in attesa di validazione ma bloccati da una verifica costo terzista ancora aperta.' },
  { key: 'sospesi_sla',            label: 'SLA sospese',      icon: <PauseCircleOutlined />,        color: '#fa8c16', help: 'Interventi con SLA sospesa: fermi in attesa di qualcosa di esterno (analisi, preventivo, ODA...), esclusi dal conteggio degli sforati.' },
  { key: 'terzisti_attivi',        label: 'Terzisti attivi',  icon: <TeamOutlined />,               color: '#13c2c2', help: 'Numero di terzisti distinti con almeno un intervento in esecuzione o da validare.' },
]

const KPI_MOCK: KpiDashboard = {
  ticket_aperti: 0,
  in_lavorazione: 0,
  in_attesa_doc: 0,
  interventi_scaduti: 0,
  interventi_chiusi_oggi: 0,
  da_validare: 0,
  in_verifica_costo: 0,
  sospesi_sla: 0,
  terzisti_attivi: 0,
}

interface AccordoScadenzaItem {
  id: string
  titolo: string
  data_fine: string
  stato: 'scaduto' | 'in_scadenza'
  cliente_id: string
  cliente_nome: string | null
}

interface AlertAttivo {
  id: string
  tipo: string
  intervento_id: string | null
  riferimento_id: string | null
  messaggio: string
  created_at: string
}

interface SuggerimentoAI {
  impianto_id: string
  nome: string
  cliente: string
  priorita: string | null
  suggerimento: any
  has_ai_suggestion: boolean
}

// 18.17.3 — l'endpoint ora restituisce il risultato precalcolato dal task
// notturno (Redis), con la data di generazione
interface ImpiantiRischioOut {
  generato_il: string | null
  items: SuggerimentoAI[]
}

export default function DashboardPage() {
  const qc = useQueryClient()
  const { data: kpi, isLoading, isError } = useQuery<KpiDashboard>({
    queryKey: ['kpi'],
    queryFn: async () => {
      const res = await client.get('/analytics/kpi')
      return res.data
    },
    refetchInterval: 30_000,
    retry: false,
  })

  const { data: accordiScadenza } = useQuery<{
    scaduti: number
    in_scadenza: number
    totale: number
    items: AccordoScadenzaItem[]
  }>({
    queryKey: ['accordi-scadenza'],
    queryFn: async () => {
      const res = await client.get('/clienti/accordi-scadenza')
      return res.data
    },
    refetchInterval: 30_000,
    retry: false,
  })

  const { data: daValidare } = useQuery({
    queryKey: ['interventi-da-validare'],
    queryFn: () => interventiApi.lista({ stato: 'da_validare', page_size: 200 }),
    refetchInterval: 30_000,
  })

  const { data: scadutiSla } = useQuery({
    queryKey: ['interventi-scaduti-sla'],
    queryFn: () => interventiApi.lista({ sla_sforato: true, page_size: 50 }),
    refetchInterval: 30_000,
  })

  // 18.51: interventi sospesi — separati dagli sforati, per non far apparire
  // come "non gestito" un intervento in realtà fermo in attesa di qualcosa
  // di esterno (analisi, preventivo, ODA...).
  const { data: sospesiSla } = useQuery({
    queryKey: ['interventi-sospesi-sla'],
    queryFn: () => interventiApi.lista({ sla_sospesa: true, page_size: 50 }),
    refetchInterval: 30_000,
  })

  const { data: risposteClienti } = useQuery<Array<{
    id: string
    numero: string
    cliente_ragione_sociale: string
    stato: 'approvato' | 'rifiutato' | 'modifiche_richieste'
    ticket_id: string | null
    updated_at: string | null
    motivo_rifiuto: string | null
    importo_stimato: number | null
  }>>({
    queryKey: ['preventivi-risposte-clienti'],
    queryFn: async () => {
      const res = await client.get('/preventivi-risposte-clienti-recenti')
      return res.data
    },
    refetchInterval: 30_000,
  })

  const { data: alertAttivi } = useQuery<AlertAttivo[]>({
    queryKey: ['alert-attivi'],
    queryFn: async () => {
      const res = await client.get('/alert')
      return res.data
    },
    refetchInterval: 60_000,
  })

  // Feedback Luca 21/09/2026: "letto" personale, distinto dal risolvere.
  // Zera il contatore SOLO per chi lo preme, l'alert resta attivo per gli altri.
  const mutSegnaVisti = useMutation({
    mutationFn: async () => (await client.post('/alert/segna-visti')).data,
    onSuccess: () => {
      message.success('Alert segnati come letti')
      qc.invalidateQueries({ queryKey: ['alert-attivi'] })
      qc.invalidateQueries({ queryKey: ['alert-count'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Errore'),
  })

  const { data: mailInAttesaCount } = useQuery({
    // Chiave DISTINTA da quella del badge sidebar (AppLayout): stessa chiave con
    // queryFn diversa (qui solo coda 'clienti', numero nudo; là totale, oggetto
    // {count}) condividerebbe la cache e romperebbe il badge — bug 13/07/2026.
    queryKey: ['notifiche-count-da-approvare', 'clienti'],
    queryFn: async () => {
      const res = await notificheApi.countDaApprovare('clienti')
      return res.count
    },
    refetchInterval: 30_000,
  })

  const { data: intakeCodaCount } = useQuery({
    queryKey: ['intake-coda-count-pending'],
    queryFn: async () => {
      return await intakeCodaApi.countPending()
    },
    refetchInterval: 30_000,
  })

  const { data: rischioData } = useQuery<ImpiantiRischioOut>({
    queryKey: ['impianti-a-rischio'],
    queryFn: async () => {
      const res = await client.get('/analytics/impianti-a-rischio')
      return res.data
    },
    // Il dato è precalcolato una volta per notte: inutile il refetch ogni 5 min
    refetchInterval: 3_600_000,
  })
  const suggerimentiAI = rischioData?.items
  const suggerimentiGeneratiIl = rischioData?.generato_il

  const navigate = useNavigate()

  const kpiData = kpi ?? KPI_MOCK

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>Dashboard</Title>

      {isError && (
        <Alert
          message="I dati KPI non sono ancora disponibili (modulo analytics non ancora attivo)."
          type="info"
          showIcon
          style={{ marginBottom: 24, borderRadius: 8 }}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        <Row gutter={[16, 16]}>
          {KPI_CONFIG.map(({ key, label, icon, color, help }) => (
            <Col xs={24} sm={12} lg={8} xl={4} key={key}>
              <Card
                style={{ borderRadius: 10, borderTop: `3px solid ${color}` }}
                styles={{ body: { padding: '20px 24px' } }}
              >
                <Statistic
                  title={<span style={{ fontSize: 13 }}>{label}<HelpTip text={help} /></span>}
                  value={kpiData[key as keyof KpiDashboard]}
                  prefix={<span style={{ color }}>{icon}</span>}
                  valueStyle={{ color, fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
          {intakeCodaCount !== undefined && intakeCodaCount > 0 && (
            <Col xs={24} sm={12} lg={8} xl={4}>
              <Card
                style={{ borderRadius: 10, borderTop: '3px solid #faad14', cursor: 'pointer' }}
                styles={{ body: { padding: '20px 24px' } }}
                onClick={() => navigate('/posta-in-arrivo')}
              >
                <Statistic
                  title={<span style={{ fontSize: 13 }}>Posta in arrivo<HelpTip text="Email in arrivo da mittenti esterni in attesa di approvazione." /></span>}
                  value={intakeCodaCount}
                  prefix={<span style={{ color: '#faad14' }}><MailOutlined /></span>}
                  valueStyle={{ color: '#faad14', fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
          )}
          {mailInAttesaCount !== undefined && mailInAttesaCount > 0 && (
            <Col xs={24} sm={12} lg={8} xl={4}>
              <Card
                style={{ borderRadius: 10, borderTop: '3px solid #ff4d4f', cursor: 'pointer' }}
                styles={{ body: { padding: '20px 24px' } }}
                onClick={() => navigate('/posta-in-uscita')}
              >
                <Statistic
                  title={<span style={{ fontSize: 13 }}>Mail in attesa<HelpTip text="Email in coda di approvazione in attesa di revisione da parte del coordinatore." /></span>}
                  value={mailInAttesaCount}
                  prefix={<span style={{ color: '#ff4d4f' }}><InboxOutlined /></span>}
                  valueStyle={{ color: '#ff4d4f', fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
          )}
          {accordiScadenza && accordiScadenza.totale > 0 && (
            <>
              {accordiScadenza.in_scadenza > 0 && (
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Card
                    style={{ borderRadius: 10, borderTop: '3px solid #fa8c16' }}
                    styles={{ body: { padding: '20px 24px' } }}
                  >
                    <Statistic
                      title={<span style={{ fontSize: 13 }}>Accordi in scadenza<HelpTip text="Accordi quadro la cui data di fine cade entro 30 giorni." /></span>}
                      value={accordiScadenza.in_scadenza}
                      prefix={<span style={{ color: '#fa8c16' }}><WarningOutlined /></span>}
                      valueStyle={{ color: '#fa8c16', fontSize: 28, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              )}
              {accordiScadenza.scaduti > 0 && (
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Card
                    style={{ borderRadius: 10, borderTop: '3px solid #ff4d4f' }}
                    styles={{ body: { padding: '20px 24px' } }}
                  >
                    <Statistic
                      title={<span style={{ fontSize: 13 }}>Accordi scaduti<HelpTip text="Accordi quadro con data di fine già superata." /></span>}
                      value={accordiScadenza.scaduti}
                      prefix={<span style={{ color: '#ff4d4f' }}><ExclamationCircleOutlined /></span>}
                      valueStyle={{ color: '#ff4d4f', fontSize: 28, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              )}
            </>
          )}
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            title={
              <span>
                <WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                Accordi quadro scaduti o in scadenza ({accordiScadenza?.totale ?? 0})
                <HelpTip text="Accordi quadro già scaduti o in scadenza entro 30 giorni. Usa «Vai» per aprire la scheda accordi del cliente." />
              </span>
            }
            style={{ borderRadius: 10 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            <Table
              size="small"
              rowKey="id"
              pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
              locale={{ emptyText: 'Nessun accordo scaduto o in scadenza — tutto in regola' }}
              dataSource={accordiScadenza?.items ?? []}
              columns={[
                {
                  title: 'Cliente',
                  dataIndex: 'cliente_nome',
                  key: 'cliente_nome',
                  render: (nome: string) => <strong>{nome}</strong>,
                },
                {
                  title: 'Accordo',
                  dataIndex: 'titolo',
                  key: 'titolo',
                },
                {
                  title: 'Scadenza',
                  dataIndex: 'data_fine',
                  key: 'data_fine',
                  width: 130,
                  render: (d: string) => formatDateTime(d),
                },
                {
                  title: 'Stato',
                  dataIndex: 'stato',
                  key: 'stato',
                  width: 130,
                  render: (s: string) => (
                    <Tag color={s === 'scaduto' ? 'red' : 'orange'}>
                      {s === 'scaduto' ? 'Scaduto' : 'In scadenza'}
                    </Tag>
                  ),
                },
                {
                  title: '',
                  key: 'azioni',
                  width: 80,
                  align: 'center' as const,
                  render: (_: unknown, r: AccordoScadenzaItem) => (
                    <Button
                      type="link"
                      size="small"
                      icon={<ArrowRightOutlined />}
                      onClick={() => navigate(`/clienti/${r.cliente_id}?tab=accordi`)}
                    >
                      Vai
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            title={
              <span>
                Interventi da validare
                <HelpTip text="Interventi terminati e documentati, in attesa di validazione dal coordinatore (stato «da validare»)." />
              </span>
            }
            style={{ borderRadius: 10 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            <Table
              size="small"
              pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
              locale={{ emptyText: 'Nessun intervento da validare' }}
              rowKey="id"
              columns={[
                { title: 'Codice',   dataIndex: 'codice',   key: 'codice',
                  render: (v: string, r: any) => (
                    <Button type="link" size="small" style={{ padding: 0 }}
                      onClick={() => navigate(`/interventi?apri_id=${r.id}`)}>
                      {v}
                    </Button>
                  )},
                { title: 'Data', key: 'data',
                  render: (_: unknown, r: any) => formatDateTime(r.data_pianificata ?? r.data_apertura) },
                { title: 'Cliente', key: 'cliente',
                  render: (_: unknown, r: any) => r.cliente?.ragione_sociale ?? '—' },
                { title: 'Punto vendita', key: 'punto_vendita',
                  render: (_: unknown, r: any) => r.punto_vendita?.nome ?? '—' },
                { title: 'Terzista', key: 'terzista',
                  render: (_: unknown, r: any) => r.terzista_ragione_sociale ?? '—' },
              ]}
              dataSource={daValidare?.items ?? []}
            />
          </Card>

          <Card
            title={
              <span>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                Interventi scaduti SLA ({scadutiSla?.items?.length ?? 0})
                <HelpTip text="Interventi con SLA superato ancora da gestire. Stessa regola della card «Scaduti SLA»: esclusi quelli in attesa documentazione, chiusi e annullati." />
              </span>
            }
            style={{ borderRadius: 10, marginTop: 16 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            <Table
              size="small"
              pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
              locale={{ emptyText: 'Nessun intervento scaduto' }}
              rowKey="id"
              columns={[
                { title: 'Codice', dataIndex: 'codice', key: 'codice',
                  render: (v: string, r: any) => (
                    <Button type="link" size="small" style={{ padding: 0 }}
                      onClick={() => navigate(`/interventi?apri_id=${r.id}`)}>
                      {v}
                    </Button>
                  )},
                { title: 'Tipologia', dataIndex: 'tipologia', key: 'tipologia' },
                { title: 'Stato', dataIndex: 'stato', key: 'stato',
                  render: (s: string) => <Tag color="red">{s}</Tag> },
                { title: 'SLA', dataIndex: 'sla_scadenza', key: 'sla_scadenza',
                  render: (d: string | null) => formatDateTime(d) },
              ]}
              dataSource={scadutiSla?.items ?? []}
            />
          </Card>

          <Card
            title={
              <span>
                <PauseCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                Attività sospese ({sospesiSla?.items?.length ?? 0})
                <HelpTip text="Interventi con SLA sospesa: fermi in attesa di qualcosa di esterno (analisi, preventivo, ODA...), sotto controllo ma esclusi dal conteggio SLA sforati." />
              </span>
            }
            style={{ borderRadius: 10, marginTop: 16 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            <Table
              size="small"
              pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
              locale={{ emptyText: 'Nessuna attività sospesa' }}
              rowKey="id"
              columns={[
                { title: 'Codice', dataIndex: 'codice', key: 'codice',
                  render: (v: string, r: any) => (
                    <Button type="link" size="small" style={{ padding: 0 }}
                      onClick={() => navigate(`/interventi?apri_id=${r.id}`)}>
                      {v}
                    </Button>
                  )},
                { title: 'Tipologia', dataIndex: 'tipologia', key: 'tipologia' },
                { title: 'Motivo', dataIndex: 'sla_sospesa_motivo', key: 'sla_sospesa_motivo',
                  render: (m: string | null) => (
                    <Tag color="orange">{MOTIVI_SOSPENSIONE_SLA.find((o) => o.value === m)?.label ?? m}</Tag>
                  ) },
                { title: 'Sospesa il', dataIndex: 'sla_sospesa_at', key: 'sla_sospesa_at',
                  render: (d: string | null) => formatDateTime(d) },
                { title: 'Ripresa prevista', dataIndex: 'sla_sospesa_fino', key: 'sla_sospesa_fino',
                  render: (d: string | null) => d ? formatDateTime(d) : 'A tempo indeterminato' },
              ]}
              dataSource={sospesiSla?.items ?? []}
            />
          </Card>

          <Card
            title={
              <span>
                <NotificationOutlined style={{ color: '#1677ff', marginRight: 8 }} />
                Risposte clienti sui preventivi (30gg) ({risposteClienti?.length ?? 0})
                <HelpTip text="Esiti dei preventivi inviati ai clienti negli ultimi 30 giorni: approvato, rifiutato o con modifiche richieste." />
              </span>
            }
            style={{ borderRadius: 10, marginTop: 16 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            <Table
              size="small"
              pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
              locale={{ emptyText: 'Nessuna risposta recente dai clienti' }}
              rowKey="id"
              columns={[
                { title: 'Cliente', dataIndex: 'cliente_ragione_sociale', key: 'cliente' },
                { title: 'Numero', dataIndex: 'numero', key: 'numero', width: 100 },
                {
                  title: 'Stato', dataIndex: 'stato', key: 'stato', width: 160,
                  render: (s: string) => {
                    const cfg: Record<string, { color: string; label: string }> = {
                      approvato: { color: 'green', label: 'Approvato' },
                      rifiutato: { color: 'red', label: 'Rifiutato' },
                      modifiche_richieste: { color: 'orange', label: 'Modifiche richieste' },
                    }
                    const c = cfg[s] || { color: 'default', label: s }
                    return <Tag color={c.color}>{c.label}</Tag>
                  },
                },
                {
                  title: 'Data', dataIndex: 'updated_at', key: 'data', width: 130,
                  render: (d: string | null) => formatDateTime(d),
                },
                {
                  title: 'Motivo', dataIndex: 'motivo_rifiuto', key: 'motivo',
                  render: (m: string | null) => m ? (
                    <span title={m} style={{ fontSize: 12 }}>
                      {m.length > 80 ? `${m.slice(0, 80)}…` : m}
                    </span>
                  ) : '—',
                },
                {
                  title: 'Azione', key: 'azione', width: 80,
                  render: (_: unknown, r: { ticket_id: string | null }) => (
                    <Button type="link" size="small" style={{ padding: 0 }}
                      onClick={() => navigate(`/ticket?apri_id=${r.ticket_id}`)} disabled={!r.ticket_id}>
                      Vai
                    </Button>
                  ),
                },
              ]}
              dataSource={risposteClienti ?? []}
            />
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={
              <span>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                Alert attivi ({alertAttivi?.length ?? 0})
                <HelpTip text="Notifiche di sistema attive: fatture non verificate e manutenzioni in scadenza. 'Segna tutto come letto' azzera il contatore solo per te; l'alert resta visibile per gli altri finché non lo leggono o qualcuno lo risolve." />
              </span>
            }
            extra={
              alertAttivi && alertAttivi.length > 0 ? (
                <Button size="small" loading={mutSegnaVisti.isPending} onClick={() => mutSegnaVisti.mutate()}>
                  Segna tutto come letto
                </Button>
              ) : undefined
            }
            style={{ borderRadius: 10 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            {!alertAttivi || alertAttivi.length === 0 ? (
              <Alert
                message="Nessun alert attivo"
                type="success"
                showIcon
                style={{ borderRadius: 8 }}
              />
            ) : (
              <List
                size="small"
                pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
                dataSource={alertAttivi}
                renderItem={(a: AlertAttivo) => (
                  <List.Item
                    actions={
                      a.intervento_id
                        ? [
                            <Button
                              type="link"
                              size="small"
                              key="vai"
                              onClick={() => navigate(`/interventi?apri_id=${a.intervento_id}`)}
                            >
                              Vai
                            </Button>,
                          ]
                        : a.tipo === 'manutenzione_scaduta' && a.riferimento_id
                        ? [
                            <Button
                              type="link"
                              size="small"
                              key="vai"
                              onClick={() => navigate(`/contratti?apri=${a.riferimento_id}`)}
                            >
                              Vai
                            </Button>,
                          ]
                        : undefined
                    }
                  >
                    <List.Item.Meta
                      avatar={<WarningOutlined style={{ color: '#fa8c16' }} />}
                      title={a.tipo.replace(/_/g, ' ')}
                      description={
                        <span style={{ fontSize: 12 }}>
                          {a.messaggio}
                          <br />
                          <span style={{ color: '#999' }}>
                            {formatDateTime(a.created_at)}
                          </span>
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <span>
                <RobotOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                Suggerimenti AI — Impianti a rischio
                <HelpTip text="Impianti segnalati dall'AI come a rischio guasto in base allo storico interventi (manutenzione predittiva)." />
              </span>
            }
            style={{ borderRadius: 10 }}
            styles={{ header: { borderBottom: '2px solid #f0f0f0' } }}
          >
            {suggerimentiGeneratiIl && (
              <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
                Aggiornato: {formatDateTime(suggerimentiGeneratiIl)} (ricalcolo automatico ogni notte)
              </div>
            )}
            {!suggerimentiAI || suggerimentiAI.length === 0 ? (
              <Alert
                message={suggerimentiGeneratiIl
                  ? 'Nessun impianto a rischio segnalato dall\'AI'
                  : 'Suggerimenti in preparazione: il primo calcolo notturno non è ancora stato eseguito'}
                type="info"
                showIcon
                style={{ borderRadius: 8 }}
              />
            ) : (
              <List
                size="small"
                pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small', showSizeChanger: false }}
                dataSource={suggerimentiAI}
                renderItem={(s: SuggerimentoAI) => {
                  const pColor =
                    s.priorita === 'Alta'
                      ? 'red'
                      : s.priorita === 'Media'
                        ? 'orange'
                        : 'green'
                  return (
                    <List.Item
                      onClick={() => navigate(`/impianti`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <List.Item.Meta
                        avatar={
                          <RobotOutlined
                            style={{
                              color: s.priorita === 'Alta' ? '#ff4d4f' : '#722ed1',
                            }}
                          />
                        }
                        title={
                          <span>
                            {s.nome}
                            {s.priorita && (
                              <Tag color={pColor} style={{ marginLeft: 8 }}>
                                {s.priorita}
                              </Tag>
                            )}
                          </span>
                        }
                        description={
                          <span style={{ fontSize: 12 }}>
                            {s.cliente}
                            {s.suggerimento?.suggerimento && (
                              <div
                                style={{
                                  maxHeight: 60,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {s.suggerimento.suggerimento}
                              </div>
                            )}
                          </span>
                        }
                      />
                    </List.Item>
                  )
                }}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
